import {
  FrequencyEnums,
  getTagsCombinationArray,
  QUARTERS_ARRAY,
  TimePeriodsEnums,
} from 'containers/Esrs';
import { getTagsCombination, mfStandardRefs, NOTAGCOMBINATION } from 'containers/Esrs/utils';
import {
  useGetMaterialStandardMetricsQuery,
  useGetRequiredStandardMaterialMetricsQuery,
} from 'models';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { SelectedRowData } from '../Metrics/MetricsTable/MetricsTableComponents/SelectedRows/SelectedRows';

export const getStandardDatapoints = (
  companyStandardId: string,
  parentStandardId: string,
  drRequiredMetrics?: string[]
) => {
  const { standardRef = '', framework = '' } = useParams();
  const isMfStandard = useMemo(() => mfStandardRefs.includes(standardRef), [standardRef]);

  const { data: requiredMetrics, loading: requiredMetricsLoading } =
    useGetRequiredStandardMaterialMetricsQuery({
      variables: {
        materialStandardId: companyStandardId,
        parentStandardId: parentStandardId || companyStandardId,
      },
      skip: !companyStandardId || !!drRequiredMetrics,
    });

  const requiredMetricRefs = useMemo(
    () =>
      drRequiredMetrics
        ? drRequiredMetrics
        : (requiredMetrics?.esrs_MaterialMetric.map((m) => m.metricRef) ?? []),
    [requiredMetrics, drRequiredMetrics]
  );

  const { data: metricsData, loading: metricDataLoading } = useGetMaterialStandardMetricsQuery({
    variables: {
      materialStandardId: companyStandardId,
      requiredMetrics: requiredMetricRefs,
    },
    skip: !companyStandardId || !requiredMetricRefs,
  });

  const metrics = useMemo(() => {
    return metricsData?.EsrsMetric.flatMap((metric) => {
      // dr - could be mfDR
      const disclosureRequirementRef = isMfStandard
        ? metric.mfDisclosureRequirements?.[0]?.mfDisclosureRequirementRef
        : metric.requirement.frameworkRef === framework
          ? metric.requirement?.reference
          : metric.mfDisclosureRequirements
              .flatMap((mfDr) => mfDr.mfDisclosureRequirement.drToMFDisclosureRequirements)
              .find((mfDr) => mfDr.disclosureRequirement.frameworkRef === framework)
              ?.disclosureRequirement.reference;
      // metric data
      const materialMetric = metric.materialMetrics?.[0];
      const isParentMetric = metric.view_childrenMetrics.length;
      const timeframes =
        materialMetric?.frequency === FrequencyEnums.yearly
          ? [TimePeriodsEnums.year]
          : QUARTERS_ARRAY;
      const tags = materialMetric?.materialMetricTags ?? [];
      const { allTagsArray } = getTagsCombinationArray(tags);
      const tagCombinations = allTagsArray.map((tagsArray) => ({
        tags: tagsArray,
        tagCombination: getTagsCombination(tagsArray),
      }));
      //   parent metric tags
      const isChildMetric = metric.parentMetrics.length;
      const parentMaterialMetric = metricsData?.EsrsMetric.find((m) =>
        metric.parentMetrics.some((p) => p.parentMetricRef === m.reference)
      )?.materialMetrics?.[0];
      const parentHasTags = parentMaterialMetric?.materialMetricTags.some(
        (m) => !!m.materialTagValues?.length
      );
      const parentTagCombinations = getTagsCombinationArray(
        parentMaterialMetric?.materialMetricTags ?? []
      ).allTagsArray.map((tagsArray) => ({
        tags: tagsArray,
        tagCombination: getTagsCombination(tagsArray),
      }));
      //   final tags - considering parent metric
      const finalTags = isChildMetric && parentHasTags ? parentTagCombinations : tagCombinations;
      if (isParentMetric) {
        const areAllChildrenQuarterly = metricsData.EsrsMetric.filter(
          (m) =>
            metric.view_childrenMetrics.some((cm) => cm.childMetricRef === m.reference) &&
            m.view_childrenMetrics.length === 0
        )?.every((m) => m.materialMetrics?.[0]?.frequency === FrequencyEnums.quarterly);
        if (areAllChildrenQuarterly)
          return QUARTERS_ARRAY.map((q) => ({
            metricRef: metric.reference,
            timeframe: q,
            tagCombination: NOTAGCOMBINATION,
            disclosureRequirementRef,
            tags: [],
          }));
        return [
          {
            metricRef: metric.reference,
            timeframe: TimePeriodsEnums.year,
            tagCombination: NOTAGCOMBINATION,
            disclosureRequirementRef,
            tags: [],
          },
        ];
      }
      const datapoints = timeframes.map((timeframe) => {
        const metricRow = {
          tagCombination: NOTAGCOMBINATION,
          metricRef: metric.reference,
          timeframe: timeframe,
          disclosureRequirementRef,
          tags: [],
        };
        const metricTagRows = finalTags?.map((combo) => ({
          tagCombination: combo.tagCombination,
          metricRef: metric.reference,
          timeframe: timeframe,
          disclosureRequirementRef,
          tags: combo.tags,
        }));

        if (!finalTags?.length) return metricRow;

        return [metricRow, ...metricTagRows];
      });
      return datapoints;
    });
  }, [metricsData]);
  return {
    metrics: metrics?.flat() as SelectedRowData[],
    loading: requiredMetricsLoading || metricDataLoading,
  };
};
